import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { StatusBadge, CarrierStatusBadge } from './StatusBadge';
import { CARRIER_STATUSES } from './load-workspace/StatusBar';
import { toast } from 'sonner';
import { Save, Share2, Copy, Check, X, ChevronDown, Search } from 'lucide-react';
import { loadAPI } from '../api';

// Appointment status options
const APPOINTMENT_STATUSES = [
  { id: 'Needs Scheduling', color: 'bg-red-500', label: 'Needs Scheduling' },
  { id: 'Requested', color: 'bg-yellow-500', label: 'Requested' },
  { id: 'Granted', color: 'bg-green-500', label: 'Granted' },
  { id: 'Updated TMS', color: 'bg-transparent border-2 border-gray-400', label: 'Updated TMS' },
];

// Clickable appointment status indicator
const AppointmentStatusSelector = ({ status, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  
  const getStatusColor = (s) => {
    switch (s) {
      case 'Needs Scheduling': return 'bg-red-500';
      case 'Requested': return 'bg-yellow-500';
      case 'Granted': return 'bg-green-500';
      case 'Updated TMS': return 'bg-transparent border-2 border-gray-400';
      default: return 'bg-gray-400';
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={`w-4 h-4 rounded-full flex-shrink-0 cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-blue-400 transition-all ${getStatusColor(status)}`}
          title={status || 'Click to set status'}
          disabled={disabled}
        />
      </PopoverTrigger>
      <PopoverContent className="w-48 p-2" align="start">
        <div className="space-y-1">
          <p className="text-xs font-semibold text-[var(--tms-text-secondary)] mb-2">Appointment Status</p>
          {APPOINTMENT_STATUSES.map((opt) => (
            <button
              key={opt.id}
              onClick={() => {
                onChange(opt.id);
                setOpen(false);
              }}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-[var(--tms-hover)] transition-colors ${status === opt.id ? 'bg-[var(--tms-hover)]' : ''}`}
            >
              <span className={`w-3 h-3 rounded-full flex-shrink-0 ${opt.color}`} />
              <span className="text-[var(--tms-text)]">{opt.label}</span>
              {status === opt.id && <Check className="w-3 h-3 ml-auto text-green-500" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Editable cell component
const EditableCell = ({ value, onChange, type = 'text', options = [], placeholder = '', className = '', disabled = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleBlur = () => {
    setIsEditing(false);
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setLocalValue(value || '');
      setIsEditing(false);
    } else if (e.key === 'Tab') {
      handleBlur();
    }
  };

  if (disabled) {
    return <span className={className}>{value || '-'}</span>;
  }

  if (type === 'select') {
    return (
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger className="h-7 text-xs border-0 bg-transparent hover:bg-[var(--tms-hover)] focus:ring-1">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {options.map(opt => (
            <SelectItem key={opt} value={opt}>{opt}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        type={type}
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className="h-7 text-xs px-1 py-0 border-blue-500 focus:ring-1 focus:ring-blue-500"
        placeholder={placeholder}
      />
    );
  }

  return (
    <span
      onClick={() => setIsEditing(true)}
      className={`cursor-text hover:bg-[var(--tms-hover)] px-1 py-0.5 rounded min-w-[2rem] block truncate ${className}`}
      title={value || "Click to edit"}
    >
      {value || <span className="text-[var(--tms-text-muted)]">{placeholder || '-'}</span>}
    </span>
  );
};

// Searchable Dropdown Component for Customers/Facilities
const SearchableDropdownCell = ({ value, displayValue, options, onSelect, placeholder = 'Select...', disabled = false, searchPlaceholder = 'Search...' }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  if (disabled) {
    return <span className="text-[var(--tms-text)] truncate block">{displayValue || '-'}</span>;
  }

  const filteredOptions = options.filter(opt => 
    opt.label.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className="flex items-center justify-between w-full px-1 py-0.5 text-left hover:bg-[var(--tms-hover)] rounded cursor-pointer min-w-0"
          title={displayValue || placeholder}
        >
          <span className="truncate text-xs">{displayValue || <span className="text-[var(--tms-text-muted)]">{placeholder}</span>}</span>
          <ChevronDown className="w-3 h-3 flex-shrink-0 ml-1 text-[var(--tms-text-muted)]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput 
            placeholder={searchPlaceholder} 
            value={search} 
            onValueChange={setSearch}
            className="h-9"
          />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup className="max-h-60 overflow-y-auto">
              {filteredOptions.map((opt) => (
                <CommandItem
                  key={opt.value}
                  value={opt.label}
                  onSelect={() => {
                    onSelect(opt.value, opt);
                    setOpen(false);
                    setSearch('');
                  }}
                  className="cursor-pointer"
                >
                  <Check className={`w-4 h-4 mr-2 ${value === opt.value ? 'opacity-100' : 'opacity-0'}`} />
                  <span className="truncate">{opt.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Editable DateTime cell component for appointment times
// Quick entry format: MMDDHHMM (e.g., "04041430" = April 4th at 14:30)
// Inline-editable appointment window end time (HHMM -> "HH:MM")
const EditableEndTimeCell = ({ value, onChange, disabled = false }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const inputRef = useRef(null);
  const toDigits = (v) => (v ? v.replace(/\D/g, '').substring(0, 4) : '');

  useEffect(() => { setLocalValue(toDigits(value)); }, [value]);
  useEffect(() => {
    if (isEditing && inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
  }, [isEditing]);

  const commit = () => {
    setIsEditing(false);
    const digits = localValue.replace(/\D/g, '');
    if (!digits) { if (value) onChange(null); return; }
    const h = parseInt(digits.substring(0, 2), 10);
    const m = parseInt(digits.substring(2, 4) || '0', 10);
    if (digits.length === 4 && h <= 23 && m <= 59) onChange(`${digits.substring(0, 2)}:${digits.substring(2, 4)}`);
    else setLocalValue(toDigits(value));
  };

  const display = value ? value.replace(':', '') : null;
  if (disabled) {
    return display ? <span className="text-xs text-[var(--tms-text-muted)] font-mono">to {display}</span> : null;
  }
  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value.replace(/\D/g, '').substring(0, 4))}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') commit(); else if (e.key === 'Escape') { setLocalValue(toDigits(value)); setIsEditing(false); } }}
        placeholder="HHMM"
        maxLength={4}
        className="h-6 text-xs px-1 py-0 border-blue-500 focus:ring-1 focus:ring-blue-500 w-12 font-mono"
      />
    );
  }
  return (
    <span
      onClick={() => setIsEditing(true)}
      className="cursor-text hover:bg-[var(--tms-hover)] px-1 py-0.5 rounded text-xs text-[var(--tms-text-muted)] font-mono whitespace-nowrap"
      title={display ? `Window ends ${display} (click to edit)` : 'Click to set end time: HHMM'}
    >
      {display ? `to ${display}` : <span className="italic opacity-60">to —</span>}
    </span>
  );
};

const EditableDateTimeCell = ({ value, onChange, disabled = false, placeholder = 'MMDDHHMM' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState('');
  const inputRef = useRef(null);

  // Convert ISO datetime to quick format for editing
  const toQuickFormat = (dateTimeStr) => {
    if (!dateTimeStr) return '';
    try {
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${month}${day}${hours}${minutes}`;
    } catch {
      return '';
    }
  };

  // Convert quick format to ISO datetime
  const fromQuickFormat = (quickStr) => {
    if (!quickStr || quickStr.length < 4) return null;
    
    // Remove any non-digit characters
    const digits = quickStr.replace(/\D/g, '');
    
    if (digits.length < 4) return null;
    
    const month = parseInt(digits.substring(0, 2), 10);
    const day = parseInt(digits.substring(2, 4), 10);
    
    // Default time to 00:00 if not provided
    let hours = 0;
    let minutes = 0;
    
    if (digits.length >= 6) {
      hours = parseInt(digits.substring(4, 6), 10);
    }
    if (digits.length >= 8) {
      minutes = parseInt(digits.substring(6, 8), 10);
    }
    
    // Validate ranges
    if (month < 1 || month > 12) return null;
    if (day < 1 || day > 31) return null;
    if (hours < 0 || hours > 23) return null;
    if (minutes < 0 || minutes > 59) return null;
    
    // Use current year
    const year = new Date().getFullYear();
    
    // Create ISO format string
    const isoStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    
    return isoStr;
  };

  useEffect(() => {
    setLocalValue(toQuickFormat(value));
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleBlur = () => {
    setIsEditing(false);
    
    if (!localValue.trim()) {
      // Clear the value if empty
      if (value) onChange(null);
      return;
    }
    
    const isoValue = fromQuickFormat(localValue);
    if (isoValue) {
      onChange(isoValue);
    } else {
      // Invalid format, revert to original
      setLocalValue(toQuickFormat(value));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleBlur();
    } else if (e.key === 'Escape') {
      setLocalValue(toQuickFormat(value));
      setIsEditing(false);
    }
  };

  // Format datetime for display (MM/DD HHMM)
  const formatDisplay = (dateTimeStr) => {
    if (!dateTimeStr) return null;
    try {
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) return null;
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${month}/${day} ${hours}${minutes}`;
    } catch {
      return null;
    }
  };

  if (disabled) {
    return (
      <span className="text-xs text-[var(--tms-text-muted)] font-mono">
        {formatDisplay(value) || '-'}
      </span>
    );
  }

  if (isEditing) {
    return (
      <Input
        ref={inputRef}
        type="text"
        value={localValue}
        onChange={(e) => {
          // Only allow digits, max 8 characters
          const digits = e.target.value.replace(/\D/g, '').substring(0, 8);
          setLocalValue(digits);
        }}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="MMDDHHMM"
        maxLength={8}
        className="h-6 text-xs px-1 py-0 border-blue-500 focus:ring-1 focus:ring-blue-500 w-20 font-mono"
      />
    );
  }

  const displayValue = formatDisplay(value);

  return (
    <span
      onClick={() => setIsEditing(true)}
      className="cursor-text hover:bg-[var(--tms-hover)] px-1 py-0.5 rounded text-xs text-[var(--tms-text-muted)] font-mono block truncate"
      title={displayValue ? `${displayValue} (click to edit)` : "Click to set: MMDDHHMM"}
    >
      {displayValue || <span className="text-[var(--tms-text-muted)] italic">{placeholder}</span>}
    </span>
  );
};

// Format facility display: City, ST ZIP - Facility Name
const formatFacilityDisplay = (stop, facilities) => {
  if (!stop) return { line1: 'N/A', line2: '' };
  
  const facility = facilities?.find(f => f.id === stop.facility_id);
  const city = facility?.city || stop.city || '';
  const state = facility?.state || stop.state || '';
  const zip = facility?.zip_code || stop.zip_code || '';
  const name = facility?.name || stop.facility_name || '';
  
  const line1 = `${city}${state ? `, ${state}` : ''}${zip ? ` ${zip}` : ''}${name ? ` - ${name}` : ''}`;
  
  let line2 = '';
  if (stop.appointment_datetime) {
    const date = new Date(stop.appointment_datetime);
    const dateStr = date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    const timeStr = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    line2 = `${dateStr} ${timeStr}`;
  }
  
  return { line1: line1 || 'N/A', line2 };
};

// Get city state for share function
const getStopCityState = (stop, facilities) => {
  if (!stop) return '';
  const facility = facilities?.find(f => f.id === stop.facility_id);
  const city = facility?.city || stop.city || '';
  const state = facility?.state || stop.state || '';
  return `${city}${state ? `, ${state}` : ''}`;
};

// Main editable row component
const EditableLoadRow = ({ 
  load, 
  facilities, 
  customers,
  onSave, 
  onDuplicate, 
  onNavigate,
  isRestrictedUser,
  activeColumns,
  allColumns,
  columnWidths = {}
}) => {
  const [editedLoad, setEditedLoad] = useState({ ...load });
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Track changes
  useEffect(() => {
    const changed = JSON.stringify(load) !== JSON.stringify(editedLoad);
    setHasChanges(changed);
  }, [load, editedLoad]);

  // Update field
  const updateField = (field, value) => {
    setEditedLoad(prev => ({ ...prev, [field]: value }));
  };

  // Update stop field
  const updateStopField = (stopIndex, field, value) => {
    setEditedLoad(prev => {
      const newStops = [...(prev.stops || [])];
      if (newStops[stopIndex]) {
        newStops[stopIndex] = { ...newStops[stopIndex], [field]: value };
      }
      return { ...prev, stops: newStops };
    });
  };

  // Save changes
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await loadAPI.update(load.id, editedLoad);
      toast.success('Load saved successfully');
      if (onSave) onSave(editedLoad);
      setHasChanges(false);
    } catch (error) {
      toast.error('Failed to save load: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIsSaving(false);
    }
  };

  // Share Load - Copy to clipboard
  const handleShareLoad = () => {
    const pickupStops = editedLoad.stops?.filter(s => s.type === 'pickup') || [];
    const deliveryStops = editedLoad.stops?.filter(s => s.type === 'delivery') || [];
    const origin = pickupStops[0];
    const lastDest = deliveryStops[deliveryStops.length - 1];
    
    const originCity = getStopCityState(origin, facilities);
    const destCity = getStopCityState(lastDest, facilities);
    
    let shareText = '';
    shareText += `${originCity} to ${destCity}\n`;
    
    const parts = [];
    if (editedLoad.weight) parts.push(`${editedLoad.weight} lbs`);
    if (editedLoad.commodity) parts.push(editedLoad.commodity);
    if (editedLoad.equipment) parts.push(editedLoad.equipment);
    if (parts.length > 0) {
      shareText += `${parts.join(', ')}\n`;
    }
    
    const pickupDateTime = origin?.appointment_datetime || editedLoad.shipping_date;
    if (pickupDateTime) {
      const pickupDate = new Date(pickupDateTime);
      const month = pickupDate.getMonth() + 1;
      const day = pickupDate.getDate();
      const hours = pickupDate.getHours().toString().padStart(2, '0');
      const mins = pickupDate.getMinutes().toString().padStart(2, '0');
      shareText += `Pickup ${month}/${day} @ ${hours}${mins}\n`;
    }
    
    if (deliveryStops.length === 1) {
      const stop = deliveryStops[0];
      if (stop.appointment_datetime) {
        const delDate = new Date(stop.appointment_datetime);
        const month = delDate.getMonth() + 1;
        const day = delDate.getDate();
        const hours = delDate.getHours().toString().padStart(2, '0');
        const mins = delDate.getMinutes().toString().padStart(2, '0');
        shareText += `Deliver ${month}/${day} @ ${hours}${mins}\n`;
      }
    } else if (deliveryStops.length > 1) {
      shareText += `Delivers:\n`;
      deliveryStops.forEach((stop) => {
        const city = getStopCityState(stop, facilities);
        if (stop.appointment_datetime) {
          const delDate = new Date(stop.appointment_datetime);
          const month = delDate.getMonth() + 1;
          const day = delDate.getDate();
          const hours = delDate.getHours().toString().padStart(2, '0');
          const mins = delDate.getMinutes().toString().padStart(2, '0');
          shareText += `${city} ${month}/${day} @ ${hours}${mins}\n`;
        } else {
          shareText += `${city} TBD\n`;
        }
      });
    }
    
    navigator.clipboard.writeText(shareText.trim())
      .then(() => toast.success('Load info copied to clipboard'))
      .catch(() => toast.error('Failed to copy load info'));
  };

  // Duplicate Load
  const handleDuplicate = async () => {
    try {
      // Create duplicate data - copy structure but clear unique fields
      const duplicateData = {
        customer_id: editedLoad.customer_id || '',
        customer_name: editedLoad.customer_name || '',
        commodity: editedLoad.commodity || 'N/A',
        equipment: editedLoad.equipment || 'Dry Van',
        weight: editedLoad.weight,
        temperature: editedLoad.temperature,
        reefer_mode: editedLoad.reefer_mode,
        status: 'Available',
        cross_border: editedLoad.cross_border || false,
        // Copy stops but clear unique fields
        stops: (editedLoad.stops || []).map(stop => ({
          type: stop.type || 'pickup',
          facility_id: stop.facility_id || '',
          facility_name: stop.facility_name || '',
          city: stop.city || '',
          state: stop.state || '',
          zip_code: stop.zip_code || '',
          appointment_status: 'Needs Scheduling',
          // Clear unique fields
          po_number: '',
          delivery_number: '',
          appointment_datetime: null,
          appt_screenshots: [],
        })),
        // Clear unique/transactional fields
        load_number: '',
        po_number: '',
        pu_number: '',
        carrier_id: null,
        carrier_name: '',
        dispatch_name: '',
        dispatch_phone: '',
        driver_name: '',
        driver_phone: '',
        truck_number: '',
        trailer_number: '',
        covered_at: null,
        rate_con_sent_at: null,
      };
      
      const response = await loadAPI.create(duplicateData);
      toast.success('Load duplicated successfully');
      if (onDuplicate) onDuplicate(response.data || response);
    } catch (error) {
      console.error('Duplicate error:', error);
      // Better error message extraction
      let errorMsg = 'Unknown error';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMsg = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMsg = error.response.data.detail.map(d => d.msg || JSON.stringify(d)).join(', ');
        } else {
          errorMsg = JSON.stringify(error.response.data.detail);
        }
      } else if (error.message) {
        errorMsg = error.message;
      }
      toast.error('Failed to duplicate load: ' + errorMsg);
    }
  };

  // Calculate totals
  // Mirrors the Load Financials panel: line haul + accessorials (+ fuel surcharge on the customer side)
  const n = (v) => parseFloat(v) || 0;
  const customerAccessorials = (editedLoad.local_accessorials || []).reduce((sum, acc) => sum + n(acc.customer_amount), 0);
  const carrierAccessorials = (editedLoad.local_accessorials || []).reduce((sum, acc) => sum + n(acc.carrier_amount), 0);
  const customerLineHaul = n(editedLoad.customer_line_haul) || (customerAccessorials ? 0 : n(editedLoad.customer_rate));
  const carrierLineHaul = n(editedLoad.carrier_line_haul) || (carrierAccessorials ? 0 : n(editedLoad.carrier_pay));
  const fscAmount = editedLoad.fsc_enabled ? n(editedLoad.fsc_amount) : 0;
  const totalCustomer = Math.round((customerLineHaul + customerAccessorials + fscAmount) * 100) / 100;
  const totalCarrier = Math.round((carrierLineHaul + carrierAccessorials) * 100) / 100;
  const margin = Math.round((totalCustomer - totalCarrier) * 100) / 100;
  const usd = (v) => Math.abs(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Get stops
  const pickupStops = editedLoad.stops?.filter(s => s.type === 'pickup') || [];
  const deliveryStops = editedLoad.stops?.filter(s => s.type === 'delivery') || [];
  const origin = pickupStops[0];
  const originDisplay = formatFacilityDisplay(origin, facilities);

  // Find stop index in original stops array
  const getStopIndex = (stop) => {
    return editedLoad.stops?.findIndex(s => s === stop) ?? -1;
  };

  // Render cell based on column ID
  const renderCell = (columnId) => {
    const column = allColumns.find(c => c.id === columnId);
    if (!column) return null;
    if (column.adminOnly && isRestrictedUser) return null;

    // Changed cells: amber/yellow background with BLACK text for good contrast in both light and dark modes
    const cellClass = hasChanges ? 'bg-amber-100 dark:bg-amber-200 !text-gray-900 [&_*]:!text-gray-900' : '';
    const cellStyle = columnWidths[columnId] 
      ? { width: `${columnWidths[columnId]}px`, minWidth: `${columnWidths[columnId]}px`, maxWidth: `${columnWidths[columnId]}px` } 
      : {};

    // Common cell classes for overflow handling
    const overflowClass = 'overflow-hidden';

    // Build customer options for dropdown
    const customerOptions = (customers || []).map(c => ({
      value: c.id,
      label: c.name,
      data: c
    }));

    // Build facility options for dropdown
    const facilityOptions = (facilities || []).map(f => ({
      value: f.id,
      label: `${f.city || ''}${f.state ? `, ${f.state}` : ''}${f.zip_code ? ` ${f.zip_code}` : ''} - ${f.name || ''}`.trim(),
      data: f
    }));

    switch (columnId) {
      case 'load_number':
        return (
          <td key={columnId} className={`text-sm ${cellClass} ${overflowClass}`} style={cellStyle}>
            <span 
              className="font-mono font-semibold cursor-pointer hover:text-blue-600 hover:underline truncate block"
              onClick={(e) => {
                e.stopPropagation();
                onNavigate(load.id);
              }}
              title={editedLoad.load_number || load.id.substring(0, 8).toUpperCase()}
              data-testid={`row-load-number-${load.id}`}
            >
              {editedLoad.load_number || load.id.substring(0, 8).toUpperCase()}
            </span>
            <div className="text-xs font-mono text-[var(--tms-text-secondary)] truncate" title={editedLoad.customer_ref ? `Customer Ref # ${editedLoad.customer_ref}` : 'No Customer Ref #'} data-testid={`row-customer-ref-${load.id}`}>
              {editedLoad.customer_ref || <span className="italic text-[var(--tms-text-muted)]">No Cust Ref #</span>}
            </div>
            <div className="text-xs text-[var(--tms-text-muted)] overflow-hidden" data-testid={`row-customer-${load.id}`}>
              <SearchableDropdownCell
                value={editedLoad.customer_id}
                displayValue={editedLoad.customer_name}
                options={customerOptions}
                onSelect={(customerId, opt) => {
                  updateField('customer_id', customerId);
                  updateField('customer_name', opt?.data?.name || '');
                }}
                placeholder="Select Customer"
                searchPlaceholder="Search customers..."
                disabled={isRestrictedUser}
              />
            </div>
          </td>
        );
      case 'origin':
        return (
          <td key={columnId} className={`text-sm ${cellClass} ${overflowClass}`} style={cellStyle}>
            <div className="flex items-start gap-2">
              <AppointmentStatusSelector
                status={origin?.appointment_status}
                onChange={(status) => {
                  const idx = editedLoad.stops?.findIndex(s => s.type === 'pickup');
                  if (idx >= 0) updateStopField(idx, 'appointment_status', status);
                }}
                disabled={isRestrictedUser}
              />
              <div className="min-w-0 flex-1 overflow-hidden">
                <SearchableDropdownCell
                  value={origin?.facility_id}
                  displayValue={originDisplay.line1}
                  options={facilityOptions}
                  onSelect={(facilityId, opt) => {
                    const idx = editedLoad.stops?.findIndex(s => s.type === 'pickup');
                    if (idx >= 0) {
                      const facility = opt?.data;
                      updateStopField(idx, 'facility_id', facilityId);
                      updateStopField(idx, 'facility_name', facility?.name || '');
                      updateStopField(idx, 'city', facility?.city || '');
                      updateStopField(idx, 'state', facility?.state || '');
                      updateStopField(idx, 'zip_code', facility?.zip_code || '');
                    }
                  }}
                  placeholder="Select Facility"
                  searchPlaceholder="Search facilities..."
                  disabled={isRestrictedUser}
                />
                <div className="flex items-center gap-1 text-xs font-mono text-[var(--tms-text-muted)]">
                  <span>PU#</span>
                  <EditableCell
                    value={origin?.references?.[0] || ''}
                    onChange={(v) => {
                      const idx = editedLoad.stops?.findIndex(s => s.type === 'pickup');
                      if (idx >= 0) updateStopField(idx, 'references', v ? [v] : []);
                    }}
                    placeholder="—"
                    disabled={isRestrictedUser}
                  />
                </div>
              </div>
            </div>
          </td>
        );
      case 'pu_appt':
        return (
          <td key={columnId} className={`text-sm font-mono ${cellClass} ${overflowClass}`} style={cellStyle} data-testid={`row-pu-appt-${load.id}`}>
            <div className="flex items-center gap-1">
              <EditableDateTimeCell
                value={origin?.appointment_datetime}
                onChange={(v) => {
                  const idx = editedLoad.stops?.findIndex(s => s.type === 'pickup');
                  if (idx >= 0) updateStopField(idx, 'appointment_datetime', v);
                }}
                disabled={isRestrictedUser}
                placeholder="MM/DD HHMM"
              />
              <EditableEndTimeCell
                value={origin?.appointment_end_time}
                onChange={(v) => {
                  const idx = editedLoad.stops?.findIndex(s => s.type === 'pickup');
                  if (idx >= 0) updateStopField(idx, 'appointment_end_time', v);
                }}
                disabled={isRestrictedUser}
              />
            </div>
          </td>
        );
      case 'destination':
        return (
          <td key={columnId} className={`text-sm ${cellClass} ${overflowClass}`} style={cellStyle}>
            <div className="space-y-1">
              {deliveryStops.length > 0 ? (
                deliveryStops.map((stop, idx) => {
                  const display = formatFacilityDisplay(stop, facilities);
                  const stopIdx = editedLoad.stops?.findIndex(s => s === stop);
                  return (
                    <div key={`${stop.facility_id || 'stop'}-${stop.stop_number ?? idx}`} className="flex items-start gap-2 min-h-[60px]">
                      <AppointmentStatusSelector
                        status={stop.appointment_status}
                        onChange={(status) => {
                          if (stopIdx >= 0) updateStopField(stopIdx, 'appointment_status', status);
                        }}
                        disabled={isRestrictedUser}
                      />
                      <div className={`min-w-0 flex-1 overflow-hidden ${idx > 0 ? 'text-xs text-[var(--tms-text-muted)]' : ''}`}>
                        <SearchableDropdownCell
                          value={stop.facility_id}
                          displayValue={display.line1}
                          options={facilityOptions}
                          onSelect={(facilityId, opt) => {
                            if (stopIdx >= 0) {
                              const facility = opt?.data;
                              updateStopField(stopIdx, 'facility_id', facilityId);
                              updateStopField(stopIdx, 'facility_name', facility?.name || '');
                              updateStopField(stopIdx, 'city', facility?.city || '');
                              updateStopField(stopIdx, 'state', facility?.state || '');
                              updateStopField(stopIdx, 'zip_code', facility?.zip_code || '');
                            }
                          }}
                          placeholder="Select Facility"
                          searchPlaceholder="Search facilities..."
                          disabled={isRestrictedUser}
                        />
                        <div className="flex items-center gap-1 text-xs font-mono text-[var(--tms-text-muted)]">
                          <span>PO#</span>
                          <EditableCell
                            value={stop.po_number}
                            onChange={(v) => { if (stopIdx >= 0) updateStopField(stopIdx, 'po_number', v); }}
                            placeholder="—"
                            disabled={isRestrictedUser}
                          />
                        </div>
                        {(stop.delivery_number || !isRestrictedUser) && (
                          <div className="flex items-center gap-1 text-xs font-mono text-[var(--tms-text-muted)]">
                            <span>DEL#</span>
                            <EditableCell
                              value={stop.delivery_number}
                              onChange={(v) => { if (stopIdx >= 0) updateStopField(stopIdx, 'delivery_number', v); }}
                              placeholder="—"
                              disabled={isRestrictedUser}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              ) : (
                <span className="text-[var(--tms-text-muted)]">N/A</span>
              )}
            </div>
          </td>
        );
      case 'del_appt':
        return (
          <td key={columnId} className={`text-sm font-mono ${cellClass} ${overflowClass}`} style={cellStyle} data-testid={`row-del-appt-${load.id}`}>
            <div className="space-y-1">
              {deliveryStops.map((stop, idx) => {
                const stopIdx = editedLoad.stops?.findIndex(s => s === stop);
                return (
                  <div key={`${stop.facility_id || 'stop'}-${stop.stop_number ?? idx}`} className={`min-h-[60px] ${idx > 0 ? 'text-xs' : ''}`}>
                    <div className="flex items-center gap-1">
                      <EditableDateTimeCell
                        value={stop.appointment_datetime}
                        onChange={(v) => { if (stopIdx >= 0) updateStopField(stopIdx, 'appointment_datetime', v); }}
                        disabled={isRestrictedUser}
                        placeholder="MM/DD HHMM"
                      />
                      <EditableEndTimeCell
                        value={stop.appointment_end_time}
                        onChange={(v) => { if (stopIdx >= 0) updateStopField(stopIdx, 'appointment_end_time', v); }}
                        disabled={isRestrictedUser}
                      />
                    </div>
                  </div>
                );
              })}
              {deliveryStops.length === 0 && '-'}
            </div>
          </td>
        );
      case 'status':
        return (
          <td key={columnId} className={`${cellClass} ${overflowClass}`} style={cellStyle} data-testid={`row-carrier-status-${load.id}`}>
            {isRestrictedUser ? (
              <CarrierStatusBadge status={editedLoad.carrier_status || 'Available'} />
            ) : (
              <Select value={editedLoad.carrier_status || 'Available'} onValueChange={(v) => updateField('carrier_status', v)}>
                <SelectTrigger className="h-7 text-xs border-0 bg-transparent hover:bg-[var(--tms-hover)] w-full max-w-[130px]">
                  <CarrierStatusBadge status={editedLoad.carrier_status || 'Available'} />
                </SelectTrigger>
                <SelectContent>
                  {CARRIER_STATUSES.map(s => (
                    <SelectItem key={s} value={s}>
                      <CarrierStatusBadge status={s} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="text-[11px] text-[var(--tms-text-secondary)] truncate px-1 mt-0.5" title={editedLoad.carrier_name || ''} data-testid={`row-carrier-name-${load.id}`}>
              {editedLoad.carrier_name || <span className="italic text-[var(--tms-text-muted)]">No carrier</span>}
            </div>
          </td>
        );
      case 'total_customer':
        return (
          <td key={columnId} className={`font-mono text-sm ${cellClass} ${overflowClass}`} style={cellStyle}>
            <span className="truncate block" title={usd(totalCustomer)} data-testid={`row-total-customer-${load.id}`}>
              {usd(totalCustomer)}
            </span>
          </td>
        );
      case 'total_carrier':
        return (
          <td key={columnId} className={`font-mono text-sm ${cellClass} ${overflowClass}`} style={cellStyle}>
            <span className="truncate block" title={usd(totalCarrier)} data-testid={`row-total-carrier-${load.id}`}>
              {usd(totalCarrier)}
            </span>
          </td>
        );
      case 'margin':
        return (
          <td key={columnId} className={`font-mono text-sm font-bold ${margin >= 0 ? 'text-green-600' : 'text-red-600'} ${cellClass} ${overflowClass}`} style={cellStyle}>
            <span className="truncate block" title={`${margin < 0 ? '-' : '+'}${usd(margin)}`} data-testid={`row-margin-${load.id}`}>
              {margin < 0 ? '-' : '+'}{usd(margin)}
            </span>
          </td>
        );
      default:
        return null;
    }
  };

  return (
    <tr 
      data-testid={`load-row-${load.id}`}
      className={`transition-colors [&>td]:align-top ${hasChanges ? 'bg-amber-50' : 'hover:bg-[var(--tms-hover)]'}`}
    >
      {activeColumns.map(columnId => renderCell(columnId))}
      
      {/* Action Buttons Column */}
      <td className="text-right whitespace-nowrap px-3 py-2" style={{ width: '140px', minWidth: '140px' }}>
        <div className="flex items-center justify-end gap-1">
          {/* Save Button - Only visible when changes exist */}
          {hasChanges && (
            <Button
              size="sm"
              variant="default"
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              disabled={isSaving}
              className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700"
              data-testid={`save-row-${load.id}`}
            >
              {isSaving ? (
                <span className="animate-spin">...</span>
              ) : (
                <>
                  <Save className="w-3 h-3 mr-1" />
                  Save
                </>
              )}
            </Button>
          )}
          
          {/* Share Load Button */}
          <Button
            size="sm"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              handleShareLoad();
            }}
            className="h-7 px-2 text-xs"
            title="Share Load"
            data-testid={`share-row-${load.id}`}
          >
            <Share2 className="w-3 h-3" />
          </Button>
          
          {/* Duplicate Button */}
          {!isRestrictedUser && (
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicate();
              }}
              className="h-7 px-2 text-xs"
              title="Duplicate Load"
              data-testid={`duplicate-row-${load.id}`}
            >
              <Copy className="w-3 h-3" />
            </Button>
          )}
        </div>
      </td>
    </tr>
  );
};

export default EditableLoadRow;
